import { useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import type { SalesWeekly } from "../lib/api.ts";
import { chartTheme } from "../lib/chart.ts";
import { ago, compact, money, num, pctValue } from "../lib/format.ts";
import { useThemeTick } from "./chrome.tsx";
import { Card, Delta, Kpi } from "./widgets.tsx";
import { Th, useSortable } from "./sortable.tsx";
import { dbLabel, plural, t, type L } from "../i18n/lang.ts";
import { AGE_BUCKET, SENTINEL, UI } from "../i18n/dict.ts";

/**
 * Недельный отчёт отдела продаж.
 *
 * Экран существует, чтобы закрыть один спор. Сейл говорит «пришло 24, закрыл
 * 8». Руководитель спрашивает: 8 из 24 или 8 из 64 — потому что кроме новых у
 * сейла лежало 40 необработанных с прошлой недели.
 *
 * Поэтому знаменатель нигде не показан готовым процентом. Он разложен на части
 * — пайплайн на понедельник плюс вошедшие за неделю, — и рядом стоит «наивная»
 * конверсия, которую называют сейлы. Разница между двумя числами и есть предмет
 * разговора; спрятать её за одним процентом означало бы оставить спор словесным.
 *
 * Вторая колонка, без которой первая читалась бы неверно: **закрытия мимо
 * воронки**. Половина закрытых лидов никогда не была на стадии Interested.
 *
 * До 08.09.2026 они не входили в числитель конверсии, и заказчик это отменил:
 * «переход сразу на успешный мы не должны считать прям плохим». Теперь главная
 * конверсия — полная, а конверсия воронки стоит рядом и говорит не о качестве
 * работы, а о том, какая её часть видна в стадиях.
 *
 * Чего в числителе быть не может — сделки без лида. Знаменатель считает лиды, и
 * сделки против него делить нельзя: замерено 58 сделок против 46 лидов за одну
 * неделю, из них 7 сделок вообще без лида. Они показаны отдельным числом.
 *
 * Депозиты этого экрана — ПЛАТЕЖИ СИСТЕМЫ БРОНИРОВАНИЯ, а не поле сделки. Это
 * два разных факта: на 134 сделках, известных обеим системам, брони дают
 * 170 204 AED против 192 206 AED по полю сделки — на 11,4 % меньше, потому что
 * регистрируется только первый холд, а доплаты менеджер вносит в CRM. Поэтому
 * подпись называет источник, а не просто «депозиты».
 *
 * И там, где системы бронирования ещё не было, стоит прочерк, а не ноль:
 * недели 2026-06-29 … 2026-08-03 печатали «0 депозитов, 0,00 AED», при том что
 * по полю сделки в те же недели депозитов создано на 27 520 … 110 679 AED.
 * Ноль читается как измеренный факт; истина там — «неизвестно».
 *
 * ЭКРАН ЦЕЛИКОМ НЕДЕЛЬНЫЙ, и с 10.09.2026 это верно не только на словах.
 * Заказчик указал, что верхний фильтр периода тут «по сути не работает»;
 * замер это подтвердил — период менял два блока из семи и не резал неделю. Теперь
 * период с экрана снят, а неделю выбирают своим фильтром: одна неделя, стрелки
 * к соседним, по умолчанию последняя ПОЛНАЯ. Все блоки — плитки, таблица по
 * паркам, разложение закрытий и табло менеджеров — считают ОДНУ И ТУ ЖЕ
 * выбранную неделю.
 */

/** Проза этого экрана. Один блок в начале файла — см. App.tsx. */
const TX = {
  /* --- плитки последней полной недели */
  vsPreviousWeek: { ru: "к прошлой неделе", en: "vs previous week" },
  workedInWeek: { ru: "В работе за неделю", en: "Worked in the week" },
  footCarriedOverMonday: { ru: "перешло с понедельника", en: "carried over Monday" },
  footEntered: { ru: "вошло", en: "entered" },
  closed: { ru: "Закрыто лидов", en: "Closed leads" },
  footThroughPipeline: { ru: "через воронку", en: "through the pipeline" },
  footOnFirstTouch: { ru: "с первого касания", en: "on first touch" },
  /* Счётное существительное: в русском три формы. */
  footDealsNoLead: {
    one: { ru: "сделка без лида", en: "deal with no lead" },
    few: { ru: "сделки без лида", en: "deals with no lead" },
    many: { ru: "сделок без лида", en: "deals with no lead" },
  },
  footOfItThroughPipeline: { ru: "из них через воронку", en: "of it through the pipeline" },
  footSalesWouldCallIt: { ru: "сейлы назвали бы это", en: "sales would call it" },
  /* Подпись плитки депозитов — общий термин `UI.depositConfirmed`. «Депозиты
     получены» стояло здесь до 10.09.2026 и не называло источник: на других
     экранах то же слово означает поле сделки и даёт другое число. */

  /* --- неделя за неделей */
  weekByWeek: { ru: "Неделя за неделей", en: "Week by week" },
  weekByWeekHint: {
    ru: "с понедельника по воскресенье, по времени парка",
    en: "Monday–Sunday, park time",
  },
  lastCompleteWeek: { ru: "последняя полная неделя", en: "last complete week" },
  rebuilt: { ru: "пересчитано", en: "rebuilt" },
  colCarriedOver: { ru: "Перешло", en: "Carried over" },
  colEntered: { ru: "Вошло", en: "Entered" },
  colWorked: { ru: "В работе", en: "Worked" },
  colViaPipeline: { ru: "через воронку", en: "via pipeline" },
  colBypassing: { ru: "мимо воронки", en: "bypassing" },
  colNoLead: { ru: "без лида", en: "no lead" },
  colOfPipeline: { ru: "от воронки", en: "of the pipeline" },
  colSalesConversion: { ru: "Конверсия «по сейлам»", en: "“Sales” conversion" },
  /* Δ — знак, а не слово: заголовки таблицы стоят `white-space: nowrap`, и
     «изменение к прошлой неделе» растянуло бы столбец на пол-таблицы. Полная
     формулировка приезжает подсказкой к заголовку. */
  colClosedDelta: { ru: "Δ закрытий", en: "Δ closed" },
  inProgress: { ru: "идёт", en: "in progress" },
  historyIncomplete: { ru: "история неполная", en: "history incomplete" },
  weekNote1: {
    ru: "= все закрытые лиды ÷ (перешло + вошло). Обе части знаменателя показаны, чтобы число можно было проверить, а не принять на веру. Лид, закрытый с первого касания, считается здесь целиком: это не худший исход, чем лид, проведённый через воронку, — часто это горячий клиент или менеджер ОП, сделавший работу за один звонок.",
    en: "= every closed lead ÷ (carried over + entered). Both parts of the denominator are shown so the number can be checked rather than believed. A lead closed on first touch counts here in full: it is not a worse outcome than one walked through the funnel — often it is a hot client, or a salesperson who did the job in one call.",
  },
  weekNote2: {
    ru: "сужает тот же знаменатель до закрытий, прошедших через Interested или Park Tour. Разрыв между двумя числами — не приговор отделу; он говорит, какая часть работы видна в стадиях.",
    en: "narrows the same denominator to closures that went through Interested or Park Tour. The gap between the two is not a verdict on the team; it says how much of the work is visible in the stages. The",
  },
  /* Со строчной в английском («The “sales” conversion»), с заглавной в русском:
     там эта врезка начинает предложение, артикля перед ней нет. */
  salesConversionInline: { ru: "Конверсия «по сейлам»", en: "“sales” conversion" },
  weekNote3: {
    ru: "делит все закрытия только на новые лиды — именно это число называют на словах, и оно самое высокое из трёх, потому что отбрасывает все лиды, перешедшие с прошлой недели.",
    en: "divides every closure by new leads only — that is the figure reported verbally, and it is the highest of the three because it drops every lead carried over from last week.",
  },
  weekNote4: {
    ru: "считает выигранные сделки, за которыми вообще нет лида, — сделку создали прямо в CRM. В конверсии этого экрана они не участвуют: конверсия считается от лидов, а лида здесь нет.",
    en: "counts won deals with no lead behind them at all — someone created the deal directly in the CRM. They take no part in this screen’s conversion: conversion is counted from leads, and here there is no lead.",
  },

  /* --- кого закрыли за неделю: вошедшие, перешедшие, мимо воронки */
  whoWasClosed: { ru: "Кого закрыли за неделю", en: "Who was closed in the week" },
  colWhoWasClosed: { ru: "Откуда пришёл закрытый лид", en: "Where the closed lead came from" },
  colShareOfClosed: { ru: "Доля закрытий", en: "Share of closures" },
  rowEnteredAndClosed: {
    ru: "Вошёл в воронку на этой же неделе — и закрыт",
    en: "Entered the pipeline this same week — and closed",
  },
  rowCarriedOverAndClosed: {
    ru: "Перешёл с прошлых периодов — и закрыт",
    en: "Carried over from earlier periods — and closed",
  },
  rowViaPipelineTotal: { ru: "Итого через воронку", en: "Through the pipeline, total" },
  rowNeverInPipeline: {
    ru: "В воронке не был ни разу",
    en: "Never in the pipeline at all",
  },
  rowTotalClosed: { ru: "Всего закрыто лидов", en: "Closed leads in total" },
  /* Остаточная строка. Появляется только когда сумма двух частей меньше
     закрытий через воронку: лид вошёл в воронку ПОЗЖЕ даты закрытия своей
     сделки, потому что сделку завели задним числом. Замерено 10.09.2026: такой
     лид в базе один и границу своей недели не перешагивает, то есть строка
     сейчас не рисуется ни на одной неделе. Молча терять его из суммы нельзя —
     тогда три числа перестанут складываться в четвёртое. */
  rowEnteredAfterClose: {
    ru: "Вошёл в воронку позже закрытия сделки",
    en: "Entered the pipeline after the deal closed" ,
  },
  rowEnteredAfterCloseHint: {
    ru: "сделка заведена задним числом — см. самопроверку витрины",
    en: "the deal was backdated — see the mart self-test",
  },
  closedSplitNote1: {
    ru: "Три числа складываются в закрытия недели. Конверсия рядом считается от другого набора лидов — тех, что стояли в воронке в понедельник или вошли за неделю, — поэтому сходиться эти цифры не обязаны.",
    en: "The three figures add up to the week’s closures. The conversion next to them is counted over a different set of leads — those standing in the pipeline on Monday or entering during the week — so the two are not meant to match.",
  },
  closedSplitNote2: {
    ru: "Выигранные сделки без лида вовсе в эти три числа не входят: считаются лиды, а за такой сделкой лида нет.",
    en: "Won deals with no lead behind them are outside these three figures: leads are what is counted, and such a deal has none.",
  },

  /* --- выбранная неделя по паркам */
  selectedWeekByPark: {
    ru: "Выбранная неделя по паркам",
    en: "Selected week by park",
  },
  /* Замерено 10.09.2026: подпись карточки печатала неделю из ОТФИЛЬТРОВАННОГО
     ряда (при июльском периоде — 2026-07-27, 25 закрытых лидов), а строки под
     ней приходили из максимума витрины (2026-08-31, 46 закрытых лидов). Теперь
     и строки, и подпись берут ВЫБРАННУЮ неделю — одну и ту же. */
  colClosedLeads: { ru: "Закрытые лиды", en: "Closed leads" },
  colOfWhichBypassing: { ru: "из них мимо воронки", en: "of which bypassing" },
  colRevenueAed: { ru: "Выручка, AED", en: "Revenue, AED" },
  colLeftInPipeline: { ru: "Осталось в воронке", en: "Left in pipeline" },

  /* --- возраст лидов в работе */
  howLongSitting: {
    ru: "Сколько лиды лежат",
    en: "How long leads have been sitting",
  },
  howLongSittingHint: {
    ru: "считается с момента входа в воронку",
    en: "counted from entering the pipeline",
  },
  /* Подписи корзин здесь СВОИ, а не из словаря AGE_BUCKET: таблица развёрнута
     по `age_order` в четыре колонки, и в шапке стоит короткая форма («0–7 d»),
     а не подпись из представления («0-7 days») — длинная растянула бы столбец. */
  colAge0_7: { ru: "0–7 дн.", en: "0–7 d" },
  colAge8_14: { ru: "8–14 дн.", en: "8–14 d" },
  colAge15_30: { ru: "15–30 дн.", en: "15–30 d" },
  colAge30plus: { ru: "30+ дн.", en: "30+ d" },
  colOldest: { ru: "Самый старый", en: "Oldest" },
  daysShort: { ru: "дн.", en: "d" },
  ageNote1: {
    ru: "Возраст считается с того момента, когда лид",
    en: "Age is counted from the moment a lead",
  },
  enteredThePipeline: { ru: "вошёл в воронку", en: "entered the pipeline" },
  ageNote2: {
    ru: ", а не с момента создания. Лид, месяц пролежавший в Inquiry, — не вина менеджера ОП, и датировать его созданием значило бы свалить этот месяц на менеджера ОП.",
    en: ", not from when it was created. A lead that sat a month in Inquiry is not the salesperson’s fault, and dating it from creation would blame them for it.",
  },

  /* --- входящие звонки, ещё не разобранные */
  inboundQueue: { ru: "Входящие звонки в ожидании", en: "Inbound calls waiting" },
  inboundQueueHint: {
    ru: "стадия Calls — обращение зарегистрировано, менеджер ОП его ещё не разбирал",
    en: "the Calls stage — logged, but no salesperson has worked it yet",
  },
  colWaiting: { ru: "Ждут", en: "Waiting" },
  colAvgAge: { ru: "Средний возраст", en: "Average age" },
  inboundTotal: { ru: "Всего в ожидании", en: "Waiting in total" },
  inboundNote1: {
    ru: "Эти лиды не попадают в таблицу возраста воронки слева: там считаются только те, кто уже стоит на стадии работы менеджера ОП. Сюда они выведены отдельно, потому что терять их нельзя, а видно их до сих пор не было нигде.",
    en: "These leads are absent from the pipeline-age table on the left: that one counts only leads already standing at a stage a salesperson works. They are shown separately here because they must not be lost, and until now they were visible nowhere.",
  },
  inboundNote2: {
    ru: "Стадию включили",
    en: "The stage was switched on",
  },
  inboundNote3: {
    ru: "— за первую же неделю на неё перевели",
    en: "— in its first week alone it received",
  },
  inboundNote4: {
    ru: "лидов одной разборкой старой базы. Поэтому старшая корзина — это один день истории, а не накопленное ожидание: и средний, и максимальный возраст в ней равны сроку с того дня. Как срок ответа на обращение эти числа можно будет читать только со следующего месяца.",
    en: "leads in a single sweep of the old base. So the oldest bucket is one day of history rather than accumulated waiting: both its average and its maximum age equal the time since that day. These figures can be read as response times only from next month on.",
  },
  transitionsWord: { ru: "переходов", en: "transitions" },

  /* --- средний чек */
  avgCheckByMonth: { ru: "Средний чек по месяцам", en: "Average check by month" },
  /* С 12.10.2026 — по месяцу праздника, с исключениями сверки (миграция 099). */
  avgCheckHint: {
    ru: "проведённые праздники по месяцу праздника, дубли и суммы-заглушки исключены",
    en: "completed parties by the month of the party, duplicates and placeholder amounts excluded",
  },
  avgCheckNote: {
    ru: "Сделки дешевле 1\u00A0AED исключены — это заглушки (58\u00A0% сделок Бахрейна несут символическую сумму вместо суммы банкета), и они утянули бы среднее в бессмыслицу. Медиана показана рядом со средним, потому что горстка крупных банкетов сдвигает среднее, а медиану — нет.",
    en: "Deals under 1 AED are excluded — they are placeholders (58 % of Bahrain deals carry a token amount rather than the banquet total) and would drag the average to meaningless. Median is shown next to the mean because a handful of large banquets moves the mean and the median does not.",
  },
  mean: { ru: "Среднее", en: "Mean" },

  /* --- табло менеджеров */
  managerScoreboard: { ru: "Табло менеджеров", en: "Manager scoreboard" },
  managerScoreboardHint: {
    ru: "воронка — на сейчас, остальное — за выбранную неделю",
    en: "pipeline is current; the rest is the selected week",
  },
  colInPipelineNow: { ru: "В воронке сейчас", en: "In pipeline now" },
  colEnteredInWeek: { ru: "Вошло за неделю", en: "Entered in the week" },
  colClosedDeals: { ru: "Завершённые праздники", en: "Completed events" },
  colOfWhichViaPipeline: { ru: "из них через воронку", en: "of which via pipeline" },
  colAvgCheckAed: { ru: "Средний чек, AED", en: "Avg check, AED" },
  colMedianDaysToClose: { ru: "Медиана дней до закрытия", en: "Median days to close" },
  hintDealsHadNoLead: {
    one: { ru: "сделка была без лида", en: "deal had no lead" },
    few: { ru: "сделки были без лида", en: "deals had no lead" },
    many: { ru: "сделок было без лида", en: "deals had no lead" },
  },
  mgrNote1: {
    ru: "считает каждый лид, который менеджер закрыл, а не только те, что проведены по стадиям, — заказчик решил так 8 сентября 2026 года. Большой разрыв между колонками",
    en: "counts every lead the manager closed, not only the ones walked through the stages — the client settled that on 8 September 2026. A large gap between",
  },
  andWord: { ru: "и", en: "and" },
  mgrNote2: {
    ru: "больше не стоит никому числителя; теперь он говорит только о том, какая часть работы видна в стадиях.",
    en: "no longer costs anyone their numerator; it now says only how much of their work is visible in the stages.",
  },
  mgrNote3: { ru: "Две оговорки.", en: "Two cautions." },
  /* С 12.10.2026 праздники, выручка и средний чек — по ДНЮ ПРАЗДНИКА, а
     закрытые лиды — по дню закрытия: сделку, которую перетащили в WON через
     недели после праздника, неделя праздника и неделя закрытия видят по-разному. */
  mgrNote4: {
    ru: "различаются по двум причинам: праздники, выручка и средний чек считаются по дню праздника, а закрытые лиды — по дню закрытия сделки; и один лид может дать несколько банкетов, а конверсия берёт лиды, потому что знаменатель считает лиды. И знаменатель намеренно смешивает часы: воронка показана",
    en: "differ for two reasons: parties, revenue and the average check count by the day of the party, closed leads by the day the deal was closed; and one lead can produce several banquets, while the conversion uses leads because the denominator counts leads. And the denominator mixes clocks on purpose: the pipeline is",
  },
  currentWord: { ru: "на сейчас", en: "current" },
  mgrNote5: {
    ru: "— а закрытия взяты за выбранную неделю, поэтому менеджер, у которого сейчас ничего не лежит, но который закрывал сделки на этой неделе, может показать больше 100\u00A0%.",
    en: "while the closures are from the selected week, so a manager holding nothing right now but closing deals that week can read above 100%.",
  },

  /* --- чего отчёт не видит */
  cannotSee: { ru: "Чего этот отчёт не видит", en: "What this report cannot see" },
  cannotSeeHint: { ru: "сказано сразу, а не по запросу", en: "stated up front, not on request" },
  depositDatesFrom: {
    ru: "Даты депозитов достоверны с",
    en: "Deposit dates are reliable from",
  },
  paymentsThroughBooking: {
    ru: "платежей через систему бронирования",
    en: "payments through the booking system",
  },
  prepayDateNote: {
    ru: "Битрикс перестал записывать дату предоплаты в июле:",
    en: "Bitrix stopped recording the prepayment date in July:",
  },
  dealsManagedHaveOne: {
    ru: "сделок рабочей эпохи имеют её.",
    en: "deals in the managed era have one.",
  },
  stageHistoryStarts: { ru: "История стадий начинается", en: "Stage history starts" },
  mondayPipelineUnknown: {
    ru: "До этой даты пайплайн на понедельник неизвестен — не ноль.",
    en: "Before that date the Monday pipeline is unknown — not zero.",
  },

  /* --- графики */
  seriesCarriedOverMonday: { ru: "Перешло с понедельника", en: "Carried over Monday" },
  seriesEnteredDuringWeek: { ru: "Вошло за неделю", en: "Entered during the week" },
  seriesConversionAll: {
    ru: "Конверсия, % (все закрытия)",
    en: "Conversion, % (all closes)",
  },
} satisfies Record<string, L | { one: L; few: L; many: L }>;

/**
 * Тот же `t` из i18n под другим именем. Внутри графиков имя `t` занято объектом
 * темы (`const t = chartTheme()`), и вызвать там перевод было бы нечем.
 */
const chartLabel = t;

const aed = (m: string | number | null | undefined): string =>
  money(m, "AED", { withCode: false });

type Week = SalesWeekly["weeks"][number];

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Работала ли система бронирования хотя бы один день этой недели.
 *
 * Замерено: недели 2026-06-29 … 2026-08-03 печатали «0 депозитов, 0,00 AED»,
 * при том что по полю сделки в те же недели депозитов создано на 27 520 /
 * 25 083 / 40 667 / 43 447 / 110 679 / 59 609 AED. Системы бронирования тогда
 * не было, и ноль в этой клетке — не измеренный факт, а отсутствие измерителя.
 * Это тот же случай, что `pipeline_known`, и он рисуется тем же приёмом.
 *
 * Правка обязана работать БЕЗ нового поля витрины, поэтому здесь два пути.
 * Признак витрины, если он уже приехал, — авторитетный: он знает не только
 * дату запуска, но и то, что первые платежи были тестовыми прогонами на
 * 0,01–1,00 AED. Пока его нет, берётся дата, которая в ответе уже есть:
 * `coverage.payments_from` = `min(paid_at)` из брони (замерено 2026-08-11).
 * Сравнивается КОНЕЦ недели, а не начало: неделя, внутри которой систему
 * включили, свои платежи всё-таки видит, и прятать её значило бы терять
 * измеренное.
 *
 * Приведение типа, а не поле в типе, — потому что `SalesWeekly` живёт в
 * `lib/api.ts`, и объявить там колонку должен тот, кто её в витрину добавляет.
 * TODO: когда `core.v_sales_week.deposits_known` доедет до `lib/api.ts`
 * (миграция 062_test_payments_are_not_deposits уже отдаёт эту колонку из
 * представления), убрать приведение и запасной путь по дате целиком.
 */
function depositsKnown(w: Week, paymentsFrom: string | null): boolean {
  const fromMart = (w as { deposits_known?: boolean }).deposits_known;
  if (typeof fromMart === "boolean") return fromMart;
  if (paymentsFrom === null || paymentsFrom === "") return true;
  return w.week_end >= paymentsFrom;
}

/**
 * Неделя, ВНУТРИ которой систему бронирования включили.
 *
 * Помечается отдельно: её число измерено не за все семь дней, и без пометки
 * низкое значение читалось бы как провал, а не как неполная неделя.
 */
function bookingStartsInWeek(w: Week, paymentsFrom: string | null): boolean {
  if (paymentsFrom === null || paymentsFrom === "") return false;
  return w.week_start <= paymentsFrom && paymentsFrom <= w.week_end;
}

/**
 * Предыдущая КАЛЕНДАРНАЯ неделя того же ряда.
 *
 * Сосед по индексу доказательством не является. Ряд приходит от свежей недели к
 * старой, но короткий период отрезает край, а неделя, по которой в витрине нет
 * ни строки, из выборки выпадает — тогда «сосед» отстоит на две недели, и
 * разница сказала бы не то, что подписано. Нет ровно семи дней — нет и значка.
 */
function previousWeek(weeks: Week[], i: number): Week | undefined {
  const cur = weeks[i];
  const p = weeks[i + 1];
  if (cur === undefined || p === undefined) return undefined;
  const gap = Date.parse(`${cur.week_start}T00:00:00Z`) - Date.parse(`${p.week_start}T00:00:00Z`);
  return gap === WEEK_MS ? p : undefined;
}

/**
 * Что уже отдаёт сервер, но ещё не объявлено в `lib/api.ts`.
 *
 * Тот же приём, что у `deposits_known` выше, и по той же причине: объявить
 * колонку в `lib/api.ts` должен владелец этого файла. Собрано в одно место,
 * чтобы приведения не расползлись по разметке.
 *
 * TODO: когда `lib/api.ts` объявит в `SalesWeekly` поля `week`, `inboundQueue`,
 * недельные `won_entered_this_week` / `won_carried_over` / `closed_split_exact` /
 * `is_selected` и покрытие `inbound_queue_from` / `inbound_queue_first_week`,
 * весь этот блок убрать. Точные типы — в отчёте волны.
 */
type WeekExtra = {
  won_entered_this_week?: number;
  won_carried_over?: number;
  closed_split_exact?: boolean;
  is_selected?: boolean;
};

function weekExtra(w: Week): WeekExtra {
  return w as Week & WeekExtra;
}

type InboundRow = {
  age_key: string;
  age_bucket: string;
  age_order: number;
  leads: number;
  avg_days: number;
  oldest_days: number;
};

type ReportExtra = {
  week?: {
    start: string;
    end: string;
    pipeline_known: boolean;
    is_last_complete: boolean;
    has_weeks: boolean;
  };
  inboundQueue?: InboundRow[];
};

type CoverageExtra = {
  inbound_queue_from?: string | null;
  inbound_queue_first_week?: number | null;
};

/**
 * Заголовок колонки с источником числа во второй строке.
 *
 * Заголовки таблицы стоят `white-space: nowrap`, и подпись источника тянула бы
 * столбец в ширину. Здесь она переносится, а столбец остаётся узким.
 */
function ColHead({ head, source }: { head: string; source: string }): React.ReactElement {
  return (
    <>
      {head}
      <div
        style={{
          fontSize: 9,
          fontWeight: 400,
          letterSpacing: "0.04em",
          color: "var(--ink-3)",
          whiteSpace: "normal",
        }}
      >
        {source}
      </div>
    </>
  );
}

/** Подпись под строкой: то же оформление, что у пометок в таблице недель. */
const rowHint: React.CSSProperties = { fontSize: 11, color: "var(--ink-3)" };

export function SalesScreen({
  report,
  compare,
}: {
  report: SalesWeekly;
  /**
   * Нажат ли чип «Сравнить с прошлой неделей».
   *
   * До 10.09.2026 разница на плитках рисовалась ВСЕГДА, а чип в шапке не делал
   * ничего: экрана не было в списке экранов со сравнением. Второй запрос тут не
   * нужен и не появляется — предыдущая неделя приезжает в том же массиве, — но
   * чип обязан переключать то, что показано, иначе он обещает управление,
   * которого нет.
   */
  compare: boolean;
}): React.ReactElement {
  const meta = (report as SalesWeekly & ReportExtra).week;

  /**
   * Выбранная неделя. Берётся из ответа, а не выбирается здесь заново: неделю
   * разрешает сервер (текущая неполная и будущая подтягиваются к последней
   * полной), и второе определение выбора означало бы ровно то расхождение,
   * которое на этом экране уже было, — подпись про одну неделю над данными
   * другой.
   *
   * Запасной путь — прежнее правило «последняя завершённая с известным
   * пайплайном»: он нужен, пока сервер не отдаёт признак, и на живых данных
   * даёт тот же ответ.
   */
  const selected =
    report.weeks.find((w) => weekExtra(w).is_selected === true) ??
    report.weeks.find((w) => w.week_start === meta?.start) ??
    report.weeks.filter((w) => !w.is_current && w.pipeline_known)[0];
  const last = selected;

  const cov = report.coverage;
  const covExtra = report.coverage as SalesWeekly["coverage"] & CoverageExtra;
  const inbound = (report as SalesWeekly & ReportExtra).inboundQueue ?? [];
  const inboundTotal = inbound.reduce((s, r) => s + r.leads, 0);

  /**
   * Прошлая неделя относительно ВЫБРАННОЙ — для значков сравнения.
   *
   * Именно относительно выбранной, а не относительно последней: иначе, уйдя
   * стрелкой на июль, человек читал бы разницу с концом августа.
   *
   * Ищется в ПОЛНОМ ряду и обязана отстоять ровно на семь дней: неделя, по
   * которой в витрине нет ни строки, из ряда выпадает, и тогда «сосед» отстоит
   * на две недели — разница сказала бы не то, что подписано. Сама она должна
   * быть полной и с известным пайплайном, иначе сравнивать не с чем.
   */
  const lastIdx =
    last === undefined ? -1 : report.weeks.findIndex((w) => w.week_start === last.week_start);
  const prevRaw = lastIdx < 0 ? undefined : previousWeek(report.weeks, lastIdx);
  const prev =
    prevRaw !== undefined && !prevRaw.is_current && prevRaw.pipeline_known ? prevRaw : undefined;

  /**
   * Значок сравнения с прошлой неделей. `undefined` — значка нет: сравнение
   * выключено чипом в шапке, или предыдущей недели в ряду просто нет — пустое
   * место честнее разницы с тем, чего не измеряли.
   */
  const cmp = (
    pick: (w: Week) => number | null,
    kind: "count" | "rate",
  ): { value: number; prev: number; kind: "count" | "rate" } | undefined => {
    if (!compare || last === undefined || prev === undefined) return undefined;
    const a = pick(last);
    const b = pick(prev);
    if (a === null || b === null) return undefined;
    return { value: a, prev: b, kind };
  };

  /* Депозиты последней недели и её предшественницы: сравнивать можно только
     когда система бронирования работала В ОБЕИХ, иначе «+70» встанет против
     неизмеренного нуля. */
  const lastDepKnown = last !== undefined && depositsKnown(last, cov.payments_from);
  const prevDepKnown = prev !== undefined && depositsKnown(prev, cov.payments_from);

  /**
   * Неделя карточки «по паркам» — ВЫБРАННАЯ, и подпись берёт её же.
   *
   * До 10.09.2026 строки приходили за максимум витрины, а подпись печаталась из
   * отфильтрованного ряда недель: при июльском периоде заголовок называл
   * 2026-07-27 (25 закрытых лидов), а под ним лежала неделя 2026-08-31 (46).
   * Теперь оба конца — один и тот же ответ сервера.
   */
  const parkWeekStart = meta?.start ?? report.byPark[0]?.week_start;
  const parkWeekEnd = meta?.end ?? last?.week_end;

  const shareOf = (part: number, whole: number): string =>
    whole === 0 ? "—" : pctValue(Math.round((1000 * part) / whole) / 10);

  /**
   * Окно графика: двенадцать недель, кончающихся ВЫБРАННОЙ.
   *
   * Ряд недель приходит теперь целиком, а не отрезком периода, и без окна
   * график был бы одинаков при любом выборе. Двенадцать — потому что квартал
   * читается глазами, а пятьдесят две недели на 260 пикселях высоты — нет.
   *
   * Недели без истории переходов из графика исключены и раньше: ноль
   * пайплайна там означает «неизвестно», и линия конверсии по такой неделе
   * нарисовала бы провал, которого не измеряли.
   */
  /**
   * Разложение закрытий выбранной недели на три части.
   *
   * Два первых числа считает витрина; третье — уже существующие закрытия мимо
   * воронки. Остаток (`closedUnexplained`) обязан быть нулём и на живых данных
   * равен нулю на всех одиннадцати неделях; он вычислен, а не предположен,
   * потому что лид может войти в воронку ПОЗЖЕ закрытия своей сделки — сделки
   * заводят задним числом. Тогда строка появится, и сумма сойдётся, вместо того
   * чтобы тихо недосчитать.
   */
  const closedEntered = last === undefined ? 0 : (weekExtra(last).won_entered_this_week ?? 0);
  const closedCarried = last === undefined ? 0 : (weekExtra(last).won_carried_over ?? 0);
  const closedUnexplained =
    last === undefined ? 0 : last.won_via_pipeline - closedEntered - closedCarried;

  const CHART_WEEKS = 12;
  const knownAsc = [...report.weeks].filter((w) => w.pipeline_known).reverse();
  const selectedAt =
    last === undefined ? -1 : knownAsc.findIndex((w) => w.week_start === last.week_start);
  const chartEnd = selectedAt < 0 ? knownAsc.length : selectedAt + 1;
  const chartWeeks = knownAsc.slice(Math.max(0, chartEnd - CHART_WEEKS), chartEnd);

  /**
   * Календарный сосед КАЖДОЙ недели, посчитанный один раз по исходному ряду.
   *
   * Значок Δ сравнивает строку с неделей на семь дней раньше, и до сортировки
   * соседа давал индекс отрисовки. После сортировки индекс означает «строка
   * выше на экране», а не «неделей раньше», и разница считалась бы с чужой
   * неделей. Ключ недели от порядка строк не зависит, поэтому соседство
   * запоминается до сортировки; сами числа те же, что печатались раньше.
   */
  const prevOf = new Map<string, Week | undefined>(
    report.weeks.map((w, i) => [w.week_start, previousWeek(report.weeks, i)]),
  );

  /*
   * Серые строки таблицы недель — «пайплайн неизвестен» и текущая неполная
   * неделя — обязаны отдавать `null` там, где ячейка печатает прочерк: иначе
   * ранняя неделя без истории переходов встала бы нулём между настоящими
   * нулями, а «неизвестно» прочиталось бы как измеренная пустая воронка.
   */
  const weekSort = useSortable(report.weeks, {
    week: { kind: "text", get: (w) => w.week_start },
    carried: { kind: "number", get: (w) => (w.pipeline_known ? w.pipeline_start : null) },
    entered: { kind: "number", get: (w) => w.entered_pipeline },
    worked: { kind: "number", get: (w) => w.worked_total },
    closed: { kind: "number", get: (w) => w.leads_won },
    viaPipeline: { kind: "number", get: (w) => w.won_via_pipeline },
    bypassing: { kind: "number", get: (w) => w.won_bypassed },
    /* Прочерк в этой колонке — оформление НУЛЯ, а не отсутствие измерения:
       «сделок без лида не было» — измеренный факт, и место такой недели среди
       нулей верное. Поэтому здесь 0, а не `null`. */
    noLead: { kind: "number", get: (w) => w.won_no_lead },
    conversion: { kind: "number", get: (w) => (w.pipeline_known ? w.conversion_full_pct : null) },
    ofPipeline: { kind: "number", get: (w) => (w.pipeline_known ? w.conversion_pct : null) },
    salesConversion: { kind: "number", get: (w) => w.conversion_naive_pct },
    /* Ноль депозитов до запуска системы бронирования — не ноль, а отсутствие
       измерителя; тем же условием, что и прочерк в ячейке. */
    deposits: {
      kind: "number",
      get: (w) => (depositsKnown(w, cov.payments_from) ? w.deposits_paid : null),
    },
    lost: { kind: "number", get: (w) => w.leads_lost },
  });

  const parkSort = useSortable(report.byPark, {
    park: { kind: "text", get: (p) => dbLabel(SENTINEL, p.park_name, p.park_name) },
    carried: { kind: "number", get: (p) => p.pipeline_start },
    entered: { kind: "number", get: (p) => p.entered_pipeline },
    worked: { kind: "number", get: (p) => p.worked_total },
    closed: { kind: "number", get: (p) => p.leads_won },
    bypassing: { kind: "number", get: (p) => p.won_bypassed },
    conversion: { kind: "number", get: (p) => p.conversion_full_pct },
    ofPipeline: { kind: "number", get: (p) => p.conversion_pct },
    deposits: { kind: "number", get: (p) => p.deposits_paid },
    revenue: { kind: "number", get: (p) => p.revenue_won_aed_minor },
    left: { kind: "number", get: (p) => p.pipeline_end },
  });

  /**
   * Старение пайплайна: строка — парк, колонки — фиксированные корзины возраста.
   *
   * Сводка считается ДО хука теми же выражениями, что стояли в разметке:
   * сортировать нужно по числам, которые печатает ячейка, а в ответе витрины их
   * нет — там отдельная строка на каждую корзину каждого парка. Порядок корзин
   * (0–7 … 30+) остаётся смыслом колонок и сортируемой колонкой не становится:
   * сортируются ПАРКИ по числу лидов в корзине.
   */
  const ageingRows = [...new Set(report.ageing.map((a) => a.park_name))].map((park) => {
    const rows = report.ageing.filter((a) => a.park_name === park);
    const at = (n: number): number =>
      rows.filter((r) => r.age_order === n).reduce((s, r) => s + r.leads, 0);
    return {
      park,
      age0_7: at(1),
      age8_14: at(2),
      age15_30: at(3),
      age30plus: at(4),
      oldest: Math.max(...rows.map((r) => r.oldest_days), 0),
    };
  });

  const ageSort = useSortable(ageingRows, {
    park: { kind: "text", get: (r) => dbLabel(SENTINEL, r.park, r.park) },
    age0_7: { kind: "number", get: (r) => r.age0_7 },
    age8_14: { kind: "number", get: (r) => r.age8_14 },
    age15_30: { kind: "number", get: (r) => r.age15_30 },
    age30plus: { kind: "number", get: (r) => r.age30plus },
    oldest: { kind: "number", get: (r) => r.oldest },
  });

  /*
   * Табло менеджеров может быть СРЕЗОМ: запрос отдаёт тридцать строк по
   * загрузке (`server/src/api/sales.ts:241—242` — `ORDER BY pipeline_now +
   * entered_period DESC LIMIT 30`), признака усечения в ответе нет. Пришло
   * меньше тридцати — отрезать было нечего, это весь отдел, и сортировать его
   * можно честно. Пришло ровно тридцать — часть отдела могла не приехать, и
   * «лучший по выручке» оказался бы лучшим из тридцати самых загруженных;
   * серверной сортировки у эндпоинта нет, поэтому заголовки выключаются сами и
   * объясняют причину. Что добавить на сервере — в отчёте волны.
   */
  const mgrSort = useSortable(
    report.managers,
    {
      manager: { kind: "text", get: (m) => dbLabel(SENTINEL, m.manager_name, m.manager_name) },
      pipelineNow: { kind: "number", get: (m) => m.pipeline_now },
      enteredPeriod: { kind: "number", get: (m) => m.entered_period },
      closedDeals: { kind: "number", get: (m) => m.won },
      closedLeads: { kind: "number", get: (m) => m.leads_won },
      viaPipeline: { kind: "number", get: (m) => m.won_via_pipeline },
      conversion: { kind: "number", get: (m) => m.conversion_pct },
      avgCheck: { kind: "number", get: (m) => m.avg_check_aed },
      medianDays: { kind: "number", get: (m) => m.median_days_to_close },
      revenue: { kind: "number", get: (m) => m.revenue_aed_minor },
    },
    { partial: report.managers.length >= 30 },
  );

  return (
    <>
      {last && (
        <div className="grid cols-4" style={{ marginBottom: 14 }}>
          {/*
            Сравнение с прошлой неделей идёт ЗНАЧКОМ плитки, тем же, что на
            остальных экранах, и стоит на всех четырёх. До 10.09.2026 разница
            была текстом и только у одной плитки из четырёх.
            Показывается, только когда нажат чип «Сравнить с прошлой неделей»:
            прежде он на этом экране висел мёртвым. Второго запроса чип не
            стоит — предыдущая неделя лежит в том же массиве недель, — но
            переключать он обязан, иначе обещает управление, которого нет.
          */}
          {/*
            Прочерк вместо числа у недели без истории переходов — то же правило,
            что в таблице ниже: пайплайн на её понедельник не «ноль», а
            неизвестен, и объём работы с ним посчитать нельзя. Такую неделю
            теперь можно выбрать руками, и без прочерка плитка показала бы
            конверсию 222 % (замер по неделе 2026-06-29: 20 закрытий против
            9 «вошедших» и неизвестного остатка).
          */}
          <Kpi
            label={t(TX.workedInWeek)}
            value={last.pipeline_known ? num(last.worked_total) : "—"}
            delta={last.pipeline_known ? cmp((w) => w.worked_total, "count") : undefined}
            foot={
              last.pipeline_known
                ? `${num(last.pipeline_start)} ${t(TX.footCarriedOverMonday)} + ${num(last.entered_pipeline)} ${t(TX.footEntered)}`
                : `${t(TX.historyIncomplete)} · ${num(last.entered_pipeline)} ${t(TX.footEntered)}`
            }
          />
          <Kpi
            label={t(TX.closed)}
            value={num(last.leads_won)}
            delta={cmp((w) => w.leads_won, "count")}
            foot={`${num(last.won_via_pipeline)} ${t(TX.footThroughPipeline)} · ${num(last.won_bypassed)} ${t(TX.footOnFirstTouch)}${
              last.won_no_lead > 0
                ? ` · ${num(last.won_no_lead)} ${plural(last.won_no_lead, TX.footDealsNoLead)}`
                : ""
            }`}
          />
          <Kpi
            label={t(UI.conversion)}
            value={
              !last.pipeline_known || last.conversion_full_pct === null
                ? "—"
                : pctValue(last.conversion_full_pct)
            }
            delta={last.pipeline_known ? cmp((w) => w.conversion_full_pct, "rate") : undefined}
            foot={`${!last.pipeline_known || last.conversion_pct === null ? "—" : pctValue(last.conversion_pct)} ${t(TX.footOfItThroughPipeline)} · ${t(TX.footSalesWouldCallIt)} ${last.conversion_naive_pct === null ? "—" : pctValue(last.conversion_naive_pct)}`}
          />
          {/*
            Плитка называет ИСТОЧНИК числа, а не просто «депозиты»: то же слово
            на других экранах означает поле сделки и даёт другое число. Замерено
            на 134 сделках, известных обеим системам: брони дают 170 204 AED
            против 192 206 AED по полю сделки — на 11,4 % меньше, потому что в
            брони регистрируется только первый холд.
          */}
          <Kpi
            label={t(UI.depositConfirmed)}
            value={lastDepKnown ? num(last.deposits_paid) : "—"}
            delta={lastDepKnown && prevDepKnown ? cmp((w) => w.deposits_paid, "count") : undefined}
            foot={
              lastDepKnown
                ? `${aed(last.deposit_aed_minor)} AED · ${t(UI.depositConfirmedShort)}`
                : t(UI.beforeBookingSystem)
            }
          />
        </div>
      )}

      <Card
        title={t(TX.weekByWeek)}
        hint={t(TX.weekByWeekHint)}
        right={
          last ? (
            <span className="hint">
              {/* Подпись называет ВЫБРАННУЮ неделю; пометка «последняя полная»
                  ставится только когда она действительно последняя полная —
                  это признак сервера, а не догадка по ряду. */}
              {meta?.is_last_complete === false ? t(UI.week) : t(TX.lastCompleteWeek)}{" "}
              {last.week_start} → {last.week_end}
              {/*
                Свежесть именно ЭТОГО отчёта. Общий индикатор в боковой панели
                говорит про 18 источников вместе, а недельные числа приходят из
                витрины, которую пересчитывают отдельно: между её пересчётом и
                свежей выгрузкой Битрикса неделя может отставать.
              */}
              {last.rebuilt_at !== null && ` · ${t(TX.rebuilt)} ${ago(last.rebuilt_at)}`}
            </span>
          ) : undefined
        }
      >
        {/*
          График кончается ВЫБРАННОЙ неделей и показывает двенадцать недель до
          неё. Ряд теперь приходит целиком (период с экрана снят), и без этого
          окна выбор недели ничего бы на графике не менял — а он обязан менять,
          иначе фильтр снова читался бы как неработающий.
        */}
        <WeeklyChart weeks={chartWeeks} />
        <div className="scroll-x" style={{ marginTop: 12 }}>
          <table className="data">
            <thead>
              <tr>
                <Th sort={weekSort} col="week">
                  {t(UI.week)}
                </Th>
                <Th sort={weekSort} col="carried" num>
                  {t(TX.colCarriedOver)}
                </Th>
                <Th sort={weekSort} col="entered" num>
                  {t(TX.colEntered)}
                </Th>
                <Th sort={weekSort} col="worked" num>
                  {t(TX.colWorked)}
                </Th>
                <Th sort={weekSort} col="closed" num>
                  {t(TX.closed)}
                </Th>
                {/*
                  Δ сортировкой не обвязана. Значок печатает то процент, то
                  штуки — `Delta` переходит на штуки, когда база меньше порога
                  (`widgets.tsx`, `SMALL_BASE`), — и одного числа, совпадающего
                  с напечатанным во всех строках, тут не существует: «+18 %»
                  (100 → 118) и «+9» (4 → 13) сравнивать нечем. Сортировать по
                  скрытой разнице значило бы дать порядок, которого на экране не
                  видно.
                */}
                {compare && (
                  <th className="num" title={t(TX.vsPreviousWeek)}>
                    {t(TX.colClosedDelta)}
                  </th>
                )}
                <Th sort={weekSort} col="viaPipeline" num>
                  {t(TX.colViaPipeline)}
                </Th>
                <Th sort={weekSort} col="bypassing" num>
                  {t(TX.colBypassing)}
                </Th>
                <Th sort={weekSort} col="noLead" num>
                  {t(TX.colNoLead)}
                </Th>
                <Th sort={weekSort} col="conversion" num>
                  {t(UI.conversion)}
                </Th>
                <Th sort={weekSort} col="ofPipeline" num>
                  {t(TX.colOfPipeline)}
                </Th>
                <Th sort={weekSort} col="salesConversion" num>
                  {t(TX.colSalesConversion)}
                </Th>
                <Th sort={weekSort} col="deposits" num>
                  <ColHead head={t(UI.deposits)} source={t(UI.depositConfirmedShort)} />
                </Th>
                <Th sort={weekSort} col="lost" num>
                  {t(UI.lost)}
                </Th>
              </tr>
            </thead>
            <tbody>
              {weekSort.rows.map((w) => {
                const p = prevOf.get(w.week_start);
                const depKnown = depositsKnown(w, cov.payments_from);
                return (
                  <tr
                    key={w.week_start}
                    style={{
                      ...(w.is_current || !w.pipeline_known ? { opacity: 0.5 } : {}),
                      /* Выбранная неделя помечена полосой слева: остальные блоки
                         экрана посчитаны по ней, и найти её строку в таблице
                         нужно глазами, а не по дате в шапке. */
                      ...(weekExtra(w).is_selected === true
                        ? { boxShadow: "inset 3px 0 0 0 var(--accent)" }
                        : {}),
                    }}
                  >
                    <td style={{ whiteSpace: "nowrap" }}>
                      {w.week_start}
                      {w.is_current && <div style={rowHint}>{t(TX.inProgress)}</div>}
                      {!w.pipeline_known && <div style={rowHint}>{t(TX.historyIncomplete)}</div>}
                      {/* Граница, за которой депозиты начинают существовать. */}
                      {bookingStartsInWeek(w, cov.payments_from) && (
                        <div style={rowHint}>{t(UI.bookingStartsHere)}</div>
                      )}
                    </td>
                    <td className="num">{w.pipeline_known ? num(w.pipeline_start) : "—"}</td>
                    <td className="num">{num(w.entered_pipeline)}</td>
                    <td className="num">
                      <b>{num(w.worked_total)}</b>
                    </td>
                    <td className="num">
                      <b>{num(w.leads_won)}</b>
                    </td>
                    {/*
                      Разница к предыдущей неделе — тем же значком, что на плитках
                      и на остальных экранах. Прочерк, когда предыдущей недели в
                      выборке нет: при коротком периоде такое бывает у самой
                      старой строки, и «+16» против пустоты было бы ложью.
                    */}
                    {compare && (
                      <td className="num">
                        {p === undefined ? (
                          "—"
                        ) : (
                          <Delta value={w.leads_won} prev={p.leads_won} kind="count" />
                        )}
                      </td>
                    )}
                    <td className="num">{num(w.won_via_pipeline)}</td>
                    {/*
                      Серость с этих двух колонок снята по решению заказчика от
                      08.09.2026: закрытие мимо воронки не хуже закрытия через
                      воронку, и красить его в цвет примечания значит подсказывать
                      обратное.
                    */}
                    <td className="num">{num(w.won_bypassed)}</td>
                    <td className="num">
                      {w.won_no_lead === 0 ? "—" : num(w.won_no_lead)}
                    </td>
                    <td className="num">
                      <b>
                        {!w.pipeline_known || w.conversion_full_pct === null
                          ? "—"
                          : pctValue(w.conversion_full_pct)}
                      </b>
                    </td>
                    <td className="num">
                      {!w.pipeline_known || w.conversion_pct === null
                        ? "—"
                        : pctValue(w.conversion_pct)}
                    </td>
                    <td className="num">
                      {w.conversion_naive_pct === null ? "—" : pctValue(w.conversion_naive_pct)}
                    </td>
                    {/* Прочерк, а не ноль: система бронирования тогда не работала. */}
                    <td className="num">{depKnown ? num(w.deposits_paid) : "—"}</td>
                    <td className="num">{num(w.leads_lost)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="note" style={{ marginTop: 12 }}>
          <b>{t(UI.conversion)}</b> {t(TX.weekNote1)}
          <br />
          <b>{t(TX.colOfPipeline)}</b> {t(TX.weekNote2)}{" "}
          <b>{t(TX.salesConversionInline)}</b> {t(TX.weekNote3)}
          <br />
          <b>{t(TX.colNoLead)}</b> {t(TX.weekNote4)}
          <br />
          <b>{t(UI.deposits)}</b> — {t(UI.depositConfirmedShort)}. {t(UI.depositTwoFacts)}{" "}
          {t(UI.beforeBookingSystem)}
        </div>
      </Card>

      {/*
        Три числа вместо двух — пункт 9 заказчика: сколько «вошедших» и сколько
        «перешедших» закрыли. Двух не хватает: замерено за неделю 2026-08-31, что
        из 46 закрытых лидов 4 вошли в воронку на этой же неделе, 20 перешли с
        прошлых периодов, а 22 не были в воронке ни разу. 4 + 20 не равно 46, и
        первым вопросом было бы «где остальные 22».

        С 10.09.2026 два первых числа приходят из витрины
        (`won_entered_this_week`, `won_carried_over`, миграция 078). Считать их
        на экране нельзя: обе части требуют истории стадий, которой у экрана
        нет.
      */}
      {/* Отбивка своим элементом: соседние карточки в этом экране сами по себе
          не разделяются, зазор задаётся снаружи — как у сетки ниже. */}
      {last && (
        <div style={{ marginTop: 14, marginBottom: 14 }}>
          <Card
            title={t(TX.whoWasClosed)}
            hint={`${last.week_start} → ${last.week_end}`}
          >
            <div className="scroll-x">
              {/*
                Разложение закрытий сортировкой не обвязано намеренно. Это не
                выборка строк, а фиксированные пункты, среди которых две
                итоговые строки — «Итого через воронку» и «Всего закрыто».
                Сортировка по величине увела бы итог в середину списка, и сумма
                перестала бы читаться суммой.
              */}
              <table className="data">
                <thead>
                  <tr>
                    <th>{t(TX.colWhoWasClosed)}</th>
                    <th className="num">{t(UI.leads)}</th>
                    <th className="num">{t(TX.colShareOfClosed)}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{t(TX.rowEnteredAndClosed)}</td>
                    <td className="num">{num(closedEntered)}</td>
                    <td className="num">{shareOf(closedEntered, last.leads_won)}</td>
                  </tr>
                  <tr>
                    <td>{t(TX.rowCarriedOverAndClosed)}</td>
                    <td className="num">{num(closedCarried)}</td>
                    <td className="num">{shareOf(closedCarried, last.leads_won)}</td>
                  </tr>
                  {/*
                    Остаток рисуется ТОЛЬКО когда он есть, и это не украшение:
                    без него три числа перестали бы складываться в четвёртое, а
                    именно на сложении и стоит смысл карточки. Появляется он у
                    сделок, заведённых задним числом — лид вошёл в воронку позже
                    закрытия своей сделки. Замерено 10.09.2026: ни на одной из
                    одиннадцати недель витрины остатка нет.
                  */}
                  {closedUnexplained !== 0 && (
                    <tr>
                      <td>
                        {t(TX.rowEnteredAfterClose)}
                        <div style={rowHint}>{t(TX.rowEnteredAfterCloseHint)}</div>
                      </td>
                      <td className="num">{num(closedUnexplained)}</td>
                      <td className="num">{shareOf(closedUnexplained, last.leads_won)}</td>
                    </tr>
                  )}
                  <tr>
                    <td>
                      <b>{t(TX.rowViaPipelineTotal)}</b>
                    </td>
                    <td className="num">
                      <b>{num(last.won_via_pipeline)}</b>
                    </td>
                    <td className="num">{shareOf(last.won_via_pipeline, last.leads_won)}</td>
                  </tr>
                  <tr>
                    <td>{t(TX.rowNeverInPipeline)}</td>
                    <td className="num">{num(last.won_bypassed)}</td>
                    <td className="num">{shareOf(last.won_bypassed, last.leads_won)}</td>
                  </tr>
                  <tr>
                    <td>
                      <b>{t(TX.rowTotalClosed)}</b>
                    </td>
                    <td className="num">
                      <b>{num(last.leads_won)}</b>
                    </td>
                    <td className="num">{shareOf(last.leads_won, last.leads_won)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="note" style={{ marginTop: 12 }}>
              {t(TX.closedSplitNote1)}
              <br />
              <b>{t(TX.colNoLead)}</b> — {t(TX.closedSplitNote2)}
            </div>
          </Card>
        </div>
      )}

      {/*
        Подпись называет неделю ТЕХ ЖЕ строк, что под ней, и обе берутся из
        одного ответа сервера. Замерено 10.09.2026: до правки при периоде
        2026-07-01 → 2026-07-31 подпись печатала 2026-07-27 (25 закрытых лидов в
        таблице недель), а строки приходили за 2026-08-31 (46 закрытых лидов по
        шести паркам) — запрос карточки брал максимум витрины и фильтр периода к
        нему не применял.
      */}
      <Card
        title={t(TX.selectedWeekByPark)}
        hint={
          parkWeekStart === undefined || parkWeekEnd === undefined
            ? undefined
            : `${parkWeekStart} → ${parkWeekEnd}`
        }
      >
        <div className="scroll-x">
          <table className="data">
            <thead>
              <tr>
                <Th sort={parkSort} col="park">
                  {t(UI.park)}
                </Th>
                <Th sort={parkSort} col="carried" num>
                  {t(TX.colCarriedOver)}
                </Th>
                <Th sort={parkSort} col="entered" num>
                  {t(TX.colEntered)}
                </Th>
                <Th sort={parkSort} col="worked" num>
                  {t(TX.colWorked)}
                </Th>
                <Th sort={parkSort} col="closed" num>
                  {t(TX.colClosedLeads)}
                </Th>
                <Th sort={parkSort} col="bypassing" num>
                  {t(TX.colOfWhichBypassing)}
                </Th>
                <Th sort={parkSort} col="conversion" num>
                  {t(UI.conversion)}
                </Th>
                <Th sort={parkSort} col="ofPipeline" num>
                  {t(TX.colOfPipeline)}
                </Th>
                <Th sort={parkSort} col="deposits" num>
                  <ColHead head={t(UI.deposits)} source={t(UI.depositConfirmedShort)} />
                </Th>
                <Th sort={parkSort} col="revenue" num>
                  {t(TX.colRevenueAed)}
                </Th>
                <Th sort={parkSort} col="left" num>
                  {t(TX.colLeftInPipeline)}
                </Th>
              </tr>
            </thead>
            <tbody>
              {parkSort.rows.map((p) => (
                <tr key={p.park_key}>
                  <td>{dbLabel(SENTINEL, p.park_name, p.park_name)}</td>
                  <td className="num">{num(p.pipeline_start)}</td>
                  <td className="num">{num(p.entered_pipeline)}</td>
                  <td className="num">
                    <b>{num(p.worked_total)}</b>
                  </td>
                  <td className="num">
                    <b>{num(p.leads_won)}</b>
                  </td>
                  <td className="num">{num(p.won_bypassed)}</td>
                  <td className="num">
                    <b>
                      {p.conversion_full_pct === null ? "—" : pctValue(p.conversion_full_pct)}
                    </b>
                  </td>
                  <td className="num">
                    {p.conversion_pct === null ? "—" : pctValue(p.conversion_pct)}
                  </td>
                  <td className="num">{num(p.deposits_paid)}</td>
                  <td className="num">{aed(p.revenue_won_aed_minor)}</td>
                  <td className="num">{num(p.pipeline_end)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid cols-2" style={{ marginTop: 14, marginBottom: 14 }}>
        <Card title={t(TX.howLongSitting)} hint={t(TX.howLongSittingHint)}>
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={ageSort} col="park">
                    {t(UI.park)}
                  </Th>
                  <Th sort={ageSort} col="age0_7" num>
                    {t(TX.colAge0_7)}
                  </Th>
                  <Th sort={ageSort} col="age8_14" num>
                    {t(TX.colAge8_14)}
                  </Th>
                  <Th sort={ageSort} col="age15_30" num>
                    {t(TX.colAge15_30)}
                  </Th>
                  <Th sort={ageSort} col="age30plus" num>
                    {t(TX.colAge30plus)}
                  </Th>
                  <Th sort={ageSort} col="oldest" num>
                    {t(TX.colOldest)}
                  </Th>
                </tr>
              </thead>
              <tbody>
                {ageSort.rows.map((r) => (
                  <tr key={r.park}>
                    <td>{dbLabel(SENTINEL, r.park, r.park)}</td>
                    <td className="num">{num(r.age0_7)}</td>
                    <td className="num">{num(r.age8_14)}</td>
                    <td className="num">{num(r.age15_30)}</td>
                    <td
                      className="num"
                      style={r.age30plus > 0 ? { color: "var(--bad)" } : undefined}
                    >
                      {num(r.age30plus)}
                    </td>
                    <td className="num">
                      {r.oldest} {t(TX.daysShort)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="note" style={{ marginTop: 12 }}>
            {t(TX.ageNote1)} <b>{t(TX.enteredThePipeline)}</b>{t(TX.ageNote2)}
          </div>
        </Card>

        {/*
          Входящие звонки стоят РЯДОМ с возрастом воронки, а не внутри него:
          корзины те же (и считаются одной функцией — `core.age_bucket_key`), но
          совокупность другая. Замерено 10.09.2026: на стадии Calls стоит 851
          лид, и ни один из них не виден в таблице слева, потому что та берёт
          только лидов, чья ТЕКУЩАЯ стадия — стадия работы менеджера ОП.
          Заказчик про эти лиды сказал: «главное чтобы мы их не теряли».

          Стадия НЕ объявлена стадией передачи в продажи, и это замер, а не
          осторожность: из 919 лидов, когда-либо бывших на Calls, до Interested
          или Park Tour дошли 19, сделок 3, выиграна 1, а сделок мимо воронки —
          ноль. Включение подняло бы «дошли до менеджера ОП» на 71 % при нуле
          новых сделок.
        */}
        <Card title={t(TX.inboundQueue)} hint={t(TX.inboundQueueHint)}>
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <th>{t(TX.colWaiting)}</th>
                  <th className="num">{t(UI.leads)}</th>
                  <th className="num">{t(TX.colAvgAge)}</th>
                  <th className="num">{t(TX.colOldest)}</th>
                </tr>
              </thead>
              <tbody>
                {/*
                  Корзины идут порядком возраста, а не по величине: порядок и
                  есть смысл этих строк. Сортировка сюда не заводится — четыре
                  фиксированные корзины и одна итоговая строка.
                */}
                {inbound.map((r) => (
                  <tr key={r.age_key}>
                    <td>{dbLabel(AGE_BUCKET, r.age_key, r.age_bucket)}</td>
                    <td
                      className="num"
                      style={r.age_key === "over_30" ? { color: "var(--bad)" } : undefined}
                    >
                      <b>{num(r.leads)}</b>
                    </td>
                    <td className="num">
                      {r.avg_days} {t(TX.daysShort)}
                    </td>
                    <td className="num">
                      {r.oldest_days} {t(TX.daysShort)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td>
                    <b>{t(TX.inboundTotal)}</b>
                  </td>
                  <td className="num">
                    <b>{num(inboundTotal)}</b>
                  </td>
                  <td className="num">—</td>
                  <td className="num">—</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="note" style={{ marginTop: 12 }}>
            {t(TX.inboundNote1)}
            <br />
            {/*
              ОГОВОРКА, без которой блок читается как катастрофа: стадию
              включили одной разборкой бэклога. Даты и числа приходят из
              запроса, а не вписаны в подпись, — захардкоженная цифра на этом
              проекте уже расходилась с данными.
            */}
            <b>
              {t(TX.inboundNote2)} {covExtra.inbound_queue_from ?? t(UI.notSet)}
            </b>{" "}
            {t(TX.inboundNote3)} {num(covExtra.inbound_queue_first_week ?? 0)}{" "}
            {t(TX.inboundNote4)}
          </div>
        </Card>
      </div>

      <div style={{ marginBottom: 14 }}>
        <Card title={t(TX.avgCheckByMonth)} hint={t(TX.avgCheckHint)}>
          <AvgCheckChart rows={report.avgCheck} />
          <div className="note" style={{ marginTop: 12 }}>
            {t(TX.avgCheckNote)}
          </div>
        </Card>
      </div>

      <Card title={t(TX.managerScoreboard)} hint={t(TX.managerScoreboardHint)}>
        <div className="scroll-x">
          <table className="data">
            <thead>
              <tr>
                <Th sort={mgrSort} col="manager">
                  {t(UI.manager)}
                </Th>
                <Th sort={mgrSort} col="pipelineNow" num>
                  {t(TX.colInPipelineNow)}
                </Th>
                <Th sort={mgrSort} col="enteredPeriod" num>
                  {t(TX.colEnteredInWeek)}
                </Th>
                <Th sort={mgrSort} col="closedDeals" num>
                  {t(TX.colClosedDeals)}
                </Th>
                <Th sort={mgrSort} col="closedLeads" num>
                  {t(TX.colClosedLeads)}
                </Th>
                <Th sort={mgrSort} col="viaPipeline" num>
                  {t(TX.colOfWhichViaPipeline)}
                </Th>
                <Th sort={mgrSort} col="conversion" num>
                  {t(UI.conversion)}
                </Th>
                <Th sort={mgrSort} col="avgCheck" num>
                  {t(TX.colAvgCheckAed)}
                </Th>
                <Th sort={mgrSort} col="medianDays" num>
                  {t(TX.colMedianDaysToClose)}
                </Th>
                <Th sort={mgrSort} col="revenue" num>
                  {t(TX.colRevenueAed)}
                </Th>
              </tr>
            </thead>
            <tbody>
              {mgrSort.rows.map((m) => (
                <tr key={m.manager_name}>
                  <td>{dbLabel(SENTINEL, m.manager_name, m.manager_name)}</td>
                  <td className="num">{num(m.pipeline_now)}</td>
                  <td className="num">{num(m.entered_period)}</td>
                  <td className="num">{num(m.won)}</td>
                  <td className="num">
                    <b>{num(m.leads_won)}</b>
                    {m.won_no_lead > 0 && (
                      <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                        {num(m.won_no_lead)} {plural(m.won_no_lead, TX.hintDealsHadNoLead)}
                      </div>
                    )}
                  </td>
                  <td className="num">{num(m.won_via_pipeline)}</td>
                  <td className="num">
                    <b>{m.conversion_pct === null ? "—" : pctValue(m.conversion_pct)}</b>
                  </td>
                  <td className="num">{m.avg_check_aed ?? "—"}</td>
                  <td className="num">{m.median_days_to_close ?? "—"}</td>
                  <td className="num">{aed(m.revenue_aed_minor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="note" style={{ marginTop: 12 }}>
          <b>{t(UI.conversion)}</b> {t(TX.mgrNote1)} <b>{t(TX.colClosedLeads)}</b>{" "}
          {t(TX.andWord)} <b>{t(TX.colOfWhichViaPipeline)}</b> {t(TX.mgrNote2)}
          <br />
          {t(TX.mgrNote3)} <b>{t(TX.colClosedDeals)}</b> {t(TX.andWord)}{" "}
          <b>{t(TX.colClosedLeads)}</b> {t(TX.mgrNote4)} <i>{t(TX.currentWord)}</i>{" "}
          {t(TX.mgrNote5)}
        </div>
      </Card>

      <Card title={t(TX.cannotSee)} hint={t(TX.cannotSeeHint)}>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: "var(--ink-2)", lineHeight: 1.7 }}>
          <li>
            <b>
              {t(TX.depositDatesFrom)} {cov.payments_from}
            </b>{" "}
            ({num(cov.payments_total)} {t(TX.paymentsThroughBooking)}). {t(TX.prepayDateNote)}{" "}
            {cov.deals_with_prepay_date} {t(UI.of)} {num(cov.deals_managed)}{" "}
            {t(TX.dealsManagedHaveOne)}
          </li>
          <li>
            <b>
              {t(TX.stageHistoryStarts)} {cov.history_from}.
            </b>{" "}
            {t(TX.mondayPipelineUnknown)}
          </li>
        </ul>
      </Card>
    </>
  );
}

/** Объём работы и результат на одной сетке: столбцы — люди, линия — процент. */
function WeeklyChart({ weeks }: { weeks: SalesWeekly["weeks"] }): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const tick = useThemeTick();

  useEffect(() => {
    if (!ref.current) return;
    /*
     * Цвета берутся здесь, а не константой в модуле: тему переключают на ходу,
     * и график должен получить те значения переменных, которые действуют в
     * момент отрисовки. Смена темы приходит через `tick` в зависимостях.
     */
    const t = chartTheme();
    const chart = echarts.init(ref.current, undefined, { renderer: "canvas" });
    chart.setOption({
      grid: { left: 46, right: 46, top: 30, bottom: 24 },
      tooltip: { trigger: "axis", ...t.tooltip },
      legend: {
        top: 0,
        textStyle: { color: t.ink2, fontFamily: t.font },
        itemHeight: 8,
        itemWidth: 12,
      },
      xAxis: {
        type: "category",
        data: weeks.map((w) => w.week_start.slice(5)),
        ...t.axis,
      },
      yAxis: [
        {
          type: "value",
          ...t.axis,
          splitLine: t.splitLine,
          axisLabel: {
            color: t.ink3,
            fontSize: 11,
            fontFamily: t.font,
            formatter: (v: number) => compact(v),
          },
        },
        {
          type: "value",
          name: "%",
          nameTextStyle: { color: t.ink3, fontSize: 10, fontFamily: t.font },
          ...t.axis,
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: chartLabel(TX.seriesCarriedOverMonday),
          type: "bar",
          stack: "work",
          /*
           * Стопка «объём работы» — холодная пара, синий снизу и фиолетовый
           * сверху, ровно как в дизайн-канвасе (там 0071CE и 7E57C5). Пара
           * намеренно приглушённая: столбцы здесь — фон вопроса, а ответ на него
           * дают бирюзовые закрытия и жёлтая линия конверсии. С розовой стопкой
           * они на графике терялись.
           */
          itemStyle: { color: t.series[5] },
          data: weeks.map((w) => w.pipeline_start),
        },
        {
          name: chartLabel(TX.seriesEnteredDuringWeek),
          type: "bar",
          stack: "work",
          itemStyle: { color: t.series[3], borderRadius: [3, 3, 0, 0] },
          data: weeks.map((w) => w.entered_pipeline),
        },
        {
          name: chartLabel(TX.closed),
          type: "bar",
          /* Закрытые лиды — результат, поэтому цвет «хорошо» из темы. */
          itemStyle: { color: t.pos, borderRadius: [3, 3, 0, 0] },
          data: weeks.map((w) => w.leads_won),
        },
        {
          name: chartLabel(TX.seriesConversionAll),
          type: "line",
          yAxisIndex: 1,
          smooth: true,
          symbolSize: 6,
          lineStyle: { width: 2 },
          /*
           * Янтарный, а не `t.accent`. Акцент темы на светлой странице
           * становится синим — ровно тем же, что у столбцов «carried over», — и
           * линия в них растворялась. `t.warn` здесь взят не как
           * предупреждение, а как единственный цвет палитры, который держит
           * контраст и на тёмном фоне (#FFB600), и на белом (#8A6000).
           */
          itemStyle: { color: t.warn },
          /*
           * Линия — ПОЛНАЯ конверсия. До 08.09.2026 здесь стояла conversion_pct,
           * то есть только закрытия через воронку, и линия читалась как «столько
           * мы конвертируем», хотя половина закрытий в неё не входила.
           */
          data: weeks.map((w) => w.conversion_full_pct),
        },
      ],
    });
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      chart.dispose();
    };
  }, [weeks, tick]);

  return <div ref={ref} style={{ height: 260 }} />;
}

function AvgCheckChart({ rows }: { rows: SalesWeekly["avgCheck"] }): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const tick = useThemeTick();

  useEffect(() => {
    if (!ref.current) return;
    const t = chartTheme();
    const chart = echarts.init(ref.current, undefined, { renderer: "canvas" });
    const recent = rows.slice(-12);
    chart.setOption({
      grid: { left: 52, right: 16, top: 30, bottom: 24 },
      tooltip: { trigger: "axis", ...t.tooltip, valueFormatter: (v: number) => `${num(v)} AED` },
      legend: {
        top: 0,
        textStyle: { color: t.ink2, fontFamily: t.font },
        itemHeight: 8,
        itemWidth: 12,
      },
      xAxis: {
        type: "category",
        data: recent.map((r) => r.month),
        ...t.axis,
      },
      yAxis: {
        type: "value",
        splitLine: t.splitLine,
        axisLabel: {
          color: t.ink3,
          fontSize: 11,
          fontFamily: t.font,
          formatter: (v: number) => compact(v),
        },
      },
      series: [
        {
          name: chartLabel(TX.mean),
          type: "line",
          smooth: true,
          symbolSize: 6,
          lineStyle: { width: 2 },
          /* Средний чек — акцентная линия: её и смотрят. Медиана рядом
             пунктиром, и ей достаётся спокойный фирменный цвет, чтобы разрыв
             между двумя линиями читался как разрыв, а не как спор цветов. */
          itemStyle: { color: t.accent },
          data: recent.map((r) => r.avg_check_aed),
        },
        {
          name: chartLabel(UI.median),
          type: "line",
          smooth: true,
          symbolSize: 5,
          lineStyle: { width: 2, type: "dashed" },
          itemStyle: { color: t.series[1] },
          data: recent.map((r) => r.median_check_aed),
        },
      ],
    });
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      chart.dispose();
    };
  }, [rows, tick]);

  return <div ref={ref} style={{ height: 220 }} />;
}
